import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, AlertTriangle } from "@/lib/icons";
import {
  getNetworkCalls,
  subscribeNetworkCalls,
  type NetworkCall,
} from "@/lib/networkGuard";

/**
 * Live readout of the runtime privacy guard.
 *
 * Subscribes to `networkGuard` so the count updates as the user navigates
 * around the app. In normal use this should stay at 0; any non-zero value
 * indicates a regression and is shown loud.
 */
export default function NetworkGuardIndicator() {
  const [calls, setCalls] = useState<ReadonlyArray<NetworkCall>>(() =>
    getNetworkCalls(),
  );

  useEffect(() => subscribeNetworkCalls(setCalls), []);

  const appCalls = useMemo(() => calls.filter((c) => c.source === "app"), [calls]);
  // Host/platform calls are still tracked by the guard (and logged to the
  // browser console) for auditability, but intentionally not rendered here
  // so the UI stays focused on calls from ElementPad's own code.
  const clean = appCalls.length === 0;

  return (
    <div
      className={`rounded-lg border p-3 ${
        clean
          ? "border-primary/30 bg-primary/5"
          : "border-destructive/40 bg-destructive/10"
      }`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2">
        {clean ? (
          <CheckCircle2 size={16} className="text-primary" aria-hidden="true" />
        ) : (
          <AlertTriangle
            size={16}
            className="text-destructive"
            aria-hidden="true"
          />
        )}
        <span className="text-sm font-semibold text-foreground">
          Live runtime check:{" "}
          {clean ? (
            <span className="text-foreground">
              0 outbound calls from ElementPad code this session
            </span>
          ) : (
            <span className="text-destructive">
              {appCalls.length} outbound call
              {appCalls.length === 1 ? "" : "s"} from app code
            </span>
          )}
        </span>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
        A guard installed at app start wraps fetch, XMLHttpRequest, WebSocket,
        EventSource, and navigator.sendBeacon. Each attempted call is counted
        and logged to the browser console (open DevTools to audit every
        call, including hosting-platform calls that are not part of
        ElementPad's source code). Counts reset when you reload.
      </p>
      {appCalls.length > 0 && (
        <ul className="mt-2 space-y-1 max-h-32 overflow-auto">
          {appCalls.slice(-10).map((c, i) => (
            <li
              key={i}
              className="font-mono text-[11px] text-destructive break-all"
            >
              {c.api} &rarr; {c.url}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}